import { Localization, SettingsManager } from "@viamap/viamap2-common"
import { useContext, useEffect, useState } from "react"
import { LayerVariabilityType } from "src/common/managers/Types"
import { SelectLayer } from "src/components/CommonInputs"
import { GlassInputControl, GlassInputGroupTitle, GlassTextInputTransformer } from "src/components/GlassInput"
import { MapitStateContext } from "src/states/MapitState"
import { read, utils } from "xlsx"

export function JobModuleJobInputs(props: { jobInputParams: { [key: string]: any } | undefined, onChange: (value: any) => void }) {
   const [state, setState] = useState<any>({})

   useEffect(() => {
      props.onChange(state)
   }, [state])

   if (props?.jobInputParams === undefined) {
      return <></>
   }

   function handleStateChange(key: string, value: any) {
      setState((prevState: any) => {
         return {
            ...prevState,
            [key]: value
         }
      })
   }

   return (
      <>
         {Object.keys(props.jobInputParams).map((key) => {
            return (
               <InputByType params={props.jobInputParams} stateValue={state} keyId={key} key={key} onChange={(e) => handleStateChange(key, e)} />
            )
         })}
      </>
   )
}

function InputByType(props: { params: any, keyId: string, stateValue: { [key: string]: any }, onChange: (value: string | fileInput) => void }) {
   let lang = Localization.getLanguage()
   let inputParams = props.params[props.keyId]
   let value = props.stateValue[props.keyId] ?? inputParams.default ?? undefined // undefined should be the default value

   let options = inputParams.options || []
   if (inputParams.type === "fileColumn") {
      options = props.stateValue[inputParams.of]?.columns || []
   }
   // let isInvalid = InputTypeValidator(props.params[props.keyId], value, options)

   if (props.params[props.keyId].hidden) {
      return <></>
   }

   switch (inputParams.type) {
      case "title":
         return <GlassInputGroupTitle>{inputParams[lang]}</GlassInputGroupTitle>
      case "date":
         return <GlassTextInputTransformer label={inputParams[lang] || props.keyId}><GlassInputControl><input value={value} onChange={(e) => props.onChange?.(e.target.value)} type="date" /></GlassInputControl></GlassTextInputTransformer>
      case "layer":
         return <GlassTextInputTransformer label={inputParams[lang] || props.keyId}><LayerSelector value={value} onSelect={(e) => props.onChange?.(e)} /></GlassTextInputTransformer>
      case "file":
         return <GlassTextInputTransformer label={inputParams[lang] || props.keyId}><FileSelector onSelect={(e) => props.onChange?.(e)} /></GlassTextInputTransformer>
      case "fileColumn":
         return options.length ? <GlassTextInputTransformer label={inputParams[lang] || props.keyId}><FileColumnSelector value={value} options={options} onSelect={props.onChange} /></GlassTextInputTransformer> : <></>
      case "string":
         return <GlassTextInputTransformer label={inputParams[lang] || props.keyId}><GlassInputControl><input className="invalid" type="text" value={value} placeholder={inputParams.placeholder} onChange={(e) => props.onChange?.(e.target.value)} /></GlassInputControl></GlassTextInputTransformer>
      case "systemSetting":
         return <SystemSettingPreview label={inputParams[lang] || props.keyId} setting={inputParams.setting} />
      default:
         return <GlassTextInputTransformer label={inputParams[lang] || props.keyId}><div></div></GlassTextInputTransformer>
   }
}




function SystemSettingPreview(props: { label: string, setting: string }) {
   if (SettingsManager.getSystemSetting(props.setting) === undefined) {
      return <><div></div><div style={{ fontSize: "12px", color: "red" }}>❗No setting found for : {props.setting}</div></>
   }
   return (<></>)
}


function LayerSelector(props: { value, onSelect: (value: string) => void }) {
   const { state } = useContext(MapitStateContext)

   return (
      <SelectLayer
         availableLayers={Object.values(state.layers).filter((layer) => layer.layerVariability !== LayerVariabilityType.FixedFeatureLayer)}
         title={"Vælg lag"}
         selectedLayerId={props.value}
         onSelect={(layerId: any) => {
            props.onSelect(layerId);
         }}
      />
   )
}

function FileColumnSelector(props: { value, options: string[], onSelect: (value: string) => void }) {
   return (
      <GlassInputControl>
         <select onChange={(e) => props.onSelect(e.target.value)} value={props.value}>
            {props.options.map((option) => {
               return (
                  <option key={option} value={option}>{option}</option>
               )
            })}
         </select>
      </GlassInputControl>
   )
}

type fileInput = {
   name: string,
   size: number,
   columns: string[],
   delimiter?: string,
   type: string,
   file: File,
}

function FileSelector(props: { onSelect: (value: fileInput) => void }) {

   async function onSelectFile(e: React.ChangeEvent<HTMLInputElement> | React.DragEventHandler<HTMLInputElement>) {
      (e as unknown as Event).stopPropagation()
      let file = (e as unknown as React.DragEvent<HTMLInputElement>).dataTransfer?.files?.[0] || (e as React.ChangeEvent<HTMLInputElement>).target.files?.[0]
      if (file === null || file === undefined) {
         return;
      }
      console.log(file.name, file.size, file.type)
      let columns: string[] = [];
      if (file.name.includes(".xlsx")) {
         let reader = new FileReader();
         columns = await new Promise<string[]>((resolve, reject) => {
            reader.onload = () => {
               let data = (reader.result as ArrayBuffer);
               let wb = read(data, { type: "array" });
               const rows = utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
               let columns = Object.keys(rows[0] as { [key: string]: unknown }) as string[];
               resolve(columns);
            }
            reader.onerror = (e) => {
               reject(e);
            }
            reader.readAsArrayBuffer(file);
         });
      }
      let delimiter: string | undefined = undefined;
      if (file.name.includes(".csv") || file.name.includes(".txt")) {
         let reader = new FileReader();
         columns = await new Promise<string[]>((resolve, reject) => {
            reader.onload = (e) => {
               let text = (e.target as FileReader).result as string;
               let lines = text.split("\n");
               delimiter = findDelimiter(lines[0]);
               let columns = lines[0].split(delimiter);
               resolve(columns);
            }
            reader.onerror = (e) => {
               reject(e);
            }
            reader.readAsText(file);
         });
      }
      let fileInfo: fileInput = {
         name: file.name,
         size: file.size,
         type: file.type,
         columns: columns,
         delimiter: delimiter,
         file: file
      }
      props.onSelect(fileInfo)

   }

   return (
      <GlassInputControl>
         <input type="file" onChange={(e) => onSelectFile(e)} onDrop={(e) => onSelectFile(e as any)} />
      </GlassInputControl>
   )
}

function findDelimiter(text: string) {
   let delimiters = [",", ";", "\t", "|"]
   let maxCount = 0
   let delimiter = ","
   for (let i = 0; i < delimiters.length; i++) {
      let count = text.split(delimiters[i]).length - 1
      if (count > maxCount) {
         maxCount = count
         delimiter = delimiters[i]
      }
   }
   return delimiter
}